import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import CustomTable from "../../../components/ui/CustomTable";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { appraisalAdminService } from "../../../services/appraisalAdminService";
import { authService } from "../../../services";
import type { SelfAppraisalData, SelfAppraisalOKR } from "../../../services/appraisalAdminService";
import ErrorState from "../../../components/ui/ErrorState";
import NoDataState from "../../../components/ui/NoDataState";
import { deriveErrorState } from "../../../utils/errorStateUtils";
import { getOkrResultColumnLabel } from "../../../utils/cycleUtils";
import { useRBAC } from "../../../hooks/useRBAC";

interface OKRRow {
  id: number;
  objective: string;
  target: string;
  result: string;
  rating: number | null;
}

interface Props {
  employeeId?: string;
}

const SelfRatingsOKRs: React.FC<Props> = ({ employeeId }) => {
  const location = useLocation();
  const params = useParams();
  const { appraisalCycle } = useRBAC();
  const resultColumnLabel = getOkrResultColumnLabel(appraisalCycle?.cycle_type);
  
  // State for API data
  const [okrData, setOkrData] = useState<SelfAppraisalData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined);

  // Get employee email from props, navigation state, or URL params
  const navState = (location.state || {}) as {
    employeeEmail?: string;
    employeeId?: string;
  };
  const [searchParams] = useSearchParams();
  
  const loggedInEmail = authService.getStoredUser()?.email ?? null;
  const selectedEmployeeId = employeeId || navState.employeeId || params.employeeId || searchParams.get("emp_id");

  const formatRating = (value: number | null | undefined): string => {
    const ratingMap: Record<number, string> = {
      1: "1-Excel",
      2: "2-Fully Met",
      3: "3-Mostly Met",
      4: "4-Requires Action",
    };
    if (value == null) {
      return "Not Rated";
    }
    const numeric = Number(value);
    return ratingMap[numeric] ?? `${numeric}`;
  };

  // Fetch OKR data from API
  const fetchOKRData = useCallback(async () => {
    if (!loggedInEmail) {
      setOkrData(null);
      setError("User email not available");
      setIsLoading(false);
      return;
    }

    if (!selectedEmployeeId) {
      setOkrData(null);
      setError("Employee ID not provided");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setErrorStatus(undefined);

      const targetEmployeeId = selectedEmployeeId as string;
      const response = await appraisalAdminService.getSelfAppraisalOKR(loggedInEmail, targetEmployeeId);

      if (response.data) {
        setOkrData(response.data);
      } else {
        setOkrData(null);
        setError("No OKR data found");
      }
    } catch (err) {
      console.error("Failed to fetch OKR data:", err);
      setOkrData(null);
      let status: number | undefined;
      let message = "Failed to load OKR data. Please try again.";

      if (err && typeof err === 'object') {
        if ('status_code' in err && typeof (err as { status_code?: unknown }).status_code === 'number') {
          status = (err as { status_code: number }).status_code;
        }
        if ('message' in err && typeof (err as { message?: unknown }).message === 'string') {
          message = (err as { message: string }).message;
        }
      } else if (err instanceof Error && err.message) {
        message = err.message;
      }

      setErrorStatus(status);
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [loggedInEmail, selectedEmployeeId]);

  useEffect(() => {
    fetchOKRData();
  }, [fetchOKRData]);

  // Helper function to retry API call
  const handleRetry = () => {
    if (!isLoading) {
      fetchOKRData();
    }
  };

  // Transform API data to component format
  const getTransformedOKRData = (): OKRRow[] => {
    if (okrData?.self_appraisal_list && okrData.self_appraisal_list.length > 0) {
      // Flatten all OKRs from all self appraisal items
      const allOKRs: OKRRow[] = [];
      let idCounter = 1;
      
      okrData.self_appraisal_list.forEach((item) => {
        if (item.okrs && item.okrs.length > 0) {
          item.okrs.forEach((okr: SelfAppraisalOKR) => {
            allOKRs.push({
              id: idCounter++,
              objective: okr.objective || "No objective provided",
              target: okr.target || "No target provided", 
              result: okr.eyr || "No result provided",
              rating: okr.rating ?? null,
            });
          });
        }
      });
      
      return allOKRs;
    }
    
    return [];
  };

  const getManagerComment = (): string => {
    const rootComment = okrData?.manager_comments;
    if (typeof rootComment === "string" && rootComment.trim()) {
      return rootComment.trim();
    }

    const appraisalWithComment = okrData?.self_appraisal_list?.find(
      (item) => typeof item.manager_comments === "string" && item.manager_comments.trim()
    );

    return appraisalWithComment?.manager_comments?.trim() ?? "";
  };

  const transformedData = getTransformedOKRData();
  const managerComment = getManagerComment();

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <LoadingSpinner className="mx-auto mb-4" />
          <p className="text-gray-600">Loading OKR data...</p>
        </div>
      </div>
    );
  }

  if (error && !okrData) {
    const { type, title, message } = deriveErrorState(error, {
      fallbackMessage: error,
      statusCode: errorStatus,
    });

    return (
      <div className="py-12">
        <ErrorState
          type={type}
          title={title}
          message={message}
          onRetry={handleRetry}
          retryLabel={isLoading ? 'Retrying...' : 'Try Again'}
          showRetryButton={!isLoading}
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <CustomTable
        columns={[
          {
            label: "SNO.",
            className:
              "px-3 py-4 text-left text-base font-medium w-16 rounded-tl-lg rounded-bl-lg",
          },
          {
            label: "Objective",
            className: "px-3 py-4 text-left text-base font-medium",
          },
          {
            label: "Target",
            className: "px-3 py-4 text-left text-base font-medium",
          },
          {
            label: resultColumnLabel,
            className: "px-3 py-4 text-left text-base font-medium",
          },
          {
            label: "Ratings",
            className:
              "px-3 py-4 text-center text-base font-medium w-44 rounded-tr-lg rounded-br-lg",
          },
        ]}
        className="w-full"
    >
      {transformedData.length === 0 ? (
        <tr>
          <td colSpan={5} className="px-4 py-12">
            <NoDataState
              title="No OKR Data Found"
              description="This employee has not submitted any OKR data yet."
            />
          </td>
        </tr>
      ) : (
        transformedData.map((r) => (
          <tr key={r.id} className="group border border-gray-200 rounded-lg overflow-hidden bg-white my-3 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-primary-300 focus-within:outline-none transition-colors duration-150">
            <td className="border-l border-t border-b border-gray-200 px-6 rounded-l-lg bg-transparent text-left align-middle">{r.id}.</td>
            <td className="border-t border-b border-gray-200 px-4 py-3 align-middle text-sm text-gray-700" style={{ whiteSpace: "pre-wrap" }}>
              {r.objective}
            </td>
            <td className="border-t border-b border-gray-200 px-4 py-3 align-middle text-sm text-gray-700" style={{ whiteSpace: "pre-wrap" }}>
              {r.target}
            </td>
            <td className="border-t border-b border-gray-200 px-4 py-3 align-middle text-sm text-gray-700" style={{ whiteSpace: "pre-wrap" }}>
              {r.result}
            </td>
            <td className="px-4 py-3 border-t border-b border-r border-gray-200 text-center align-middle rounded-r-lg">
              <span className="inline-flex items-center justify-center min-w-[3.5rem] h-8 rounded-full bg-purple-100 text-purple-700 text-sm font-semibold px-2">
                {formatRating(r.rating)}
              </span>
            </td>
          </tr>
        ))
      )}
    </CustomTable>
    {managerComment && (
      <div className="mt-5 rounded-lg border border-purple-100 bg-white shadow-sm">
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="text-sm font-semibold text-gray-900">Manager Comments</div>
          <div className="mt-0.5 text-xs text-gray-500">Saved feedback for this OKR rating</div>
        </div>
        <div className="whitespace-pre-wrap px-4 py-4 text-sm leading-relaxed text-gray-700">
          {managerComment}
        </div>
      </div>
    )}
    </div>
  );
};

export default SelfRatingsOKRs;
